import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, X, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { inputCls, fullUrl, canDo } from "./shared";

export function MediaTab({ user }) {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const canEdit = canDo(user, "content", "edit");

  const load = () => {
    const params = {};
    if (folder) params.folder = folder;
    if (q) params.q = q;
    api.get("/admin/media", { params }).then((r) => { setItems(r.data.items); setFolders(r.data.folders); })
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, [folder]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDetail(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", folder || "Uncategorized");
        await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length > 1 ? `${files.length} assets uploaded` : "Asset uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveDetail = async () => {
    try {
      await api.put(`/admin/media/${detail.id}`, { name: detail.name, folder: detail.folder });
      toast.success("Asset updated");
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove "${m.name}" from the library? Sections already using it keep their copy of the image.`)) return;
    await api.delete(`/admin/media/${m.id}`).catch((e) => toast.error(formatApiError(e)));
    setDetail(null);
    load();
  };

  return (
    <div data-testid="media-tab">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button onClick={() => setFolder("")} className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${!folder ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20"}`}>All</button>
        {folders.map((f) => (
          <button key={f} data-testid={`media-folder-${f.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setFolder(f)}
            className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${folder === f ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20"}`}>{f}</button>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 min-w-40">
          <input data-testid="media-search" value={q} onChange={(e) => setQ(e.target.value)} onBlur={load} placeholder="Search by name" className={inputCls} />
        </form>
        {canEdit && (
          <>
            <button data-testid="media-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="vv-btn-dark !px-4">
              <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="media-upload-input" />
          </>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {items.map((m) => (
          <button key={m.id} data-testid={`media-card-${m.id}`} onClick={() => setDetail({ ...m })} className="text-left group">
            <div className="aspect-square overflow-hidden bg-vv-elevated">
              <img src={fullUrl(m.url)} alt={m.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <p className="text-xs mt-2 truncate">{m.name}</p>
            <p className="text-[10px] uppercase tracking-widest text-vv-stone">{m.folder}{m.width ? ` · ${m.width}×${m.height}` : ""}</p>
          </button>
        ))}
        {items.length === 0 && <p data-testid="media-empty" className="col-span-full text-sm text-vv-muted py-12 text-center">No assets in this folder yet.</p>}
      </div>

      {detail && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-vv-black/50" onClick={() => setDetail(null)} />
          <div data-testid="media-detail" className="relative bg-white w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <p className="font-display text-xl">Asset Details</p>
              <button onClick={() => setDetail(null)} aria-label="Close"><X size={20} strokeWidth={1.25} /></button>
            </div>
            <div className="aspect-video bg-vv-elevated overflow-hidden">
              <img src={fullUrl(detail.url)} alt={detail.name} className="w-full h-full object-contain" />
            </div>
            <fieldset disabled={!canEdit} className="space-y-4">
              <input data-testid="media-detail-name" value={detail.name} onChange={(e) => setDetail({ ...detail, name: e.target.value })} className={inputCls} />
              <input data-testid="media-detail-folder" value={detail.folder || ""} onChange={(e) => setDetail({ ...detail, folder: e.target.value })} placeholder="Folder" className={inputCls} />
            </fieldset>
            <p className="text-[11px] text-vv-muted font-mono break-all">{detail.url}</p>
            <div className="flex justify-between items-center">
              <button onClick={() => { navigator.clipboard?.writeText(fullUrl(detail.url)); toast.success("URL copied"); }} className="vv-btn-outline-dark !px-4"><Copy size={12} /> Copy URL</button>
              {canEdit && (
                <div className="flex gap-2">
                  <button data-testid="media-detail-delete" onClick={() => remove(detail)} className="p-2 text-vv-burgundy" aria-label="Delete asset"><Trash2 size={16} strokeWidth={1.5} /></button>
                  <button data-testid="media-detail-save" onClick={saveDetail} className="vv-btn-dark !px-5">Save</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
